import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { uniq } from 'lodash';
import { CSV_FILE_TYPES } from 'config/accepted-file-types';
import { FileUploader } from 'components/v2/FileUploader';
import LoadingCircle from 'components/ui/LoadingCircle';
import Alert from 'components/ui/Alert';
import {
  runPrecheck,
  resendEDIInvoices,
  resendEDIInvoicesBulk,
} from 'utils/ShipmentWebAPIUtils';
import { sluggify } from 'utils/localization/utils';
import { convertCommaSeparatedStringToArray } from 'helpers/strings';
import { showToast } from 'stores/ToastStore';
import ReferenceNumberTable from './ReferenceNumberTable';
import CustomerStatus from './CustomerStatus';

export default function Invoices() {
  const { t } = useTranslation();
  const [ranPrecheck, setRanPrecheck] = useState(false);
  const [errors, setErrors] = useState({});
  const [runningPrecheck, setRunningPrecheck] = useState(false);
  const [resendingEvents, setResendingEvents] = useState(false);
  const [results, setResults] = useState([]);
  const [resendEDISuccess, setResendEDISuccess] = useState(false);
  const [resendEDISuccessMessage, setResendEDISuccessMessage] = useState('');
  const [resentEDIError, setResendEDIError] = useState(false);
  const [shipperStatusList, setShipperStatusList] = useState([]);
  const [showRunPrecheckBanner, setShowRunPrecheckBanner] = useState(true);
  const [invoices, setInvoices] = useState('');
  const [invoicesList, setInvoicesList] = useState([]);

  const [uploadPending, setUploadPending] = useState(false);
  const [files, setFiles] = useState([]);
  const fileUploaderRef = useRef(null);

  useEffect(() => {
    document.title = `${t('RESEND_EDI')} - ${t('INVOICE', { count: 2 })}`;
  }, []);

  const removeInvoice = (i) => {
    const filteredList = invoicesList.filter((e) => e !== i);
    setInvoicesList(filteredList);
    setRanPrecheck(false);
  };
  const onChangeInvoices = (e) => {
    setInvoices(e.target.value);
  };
  const addInvoices = () => {
    const newInvoices = convertCommaSeparatedStringToArray(invoices);
    setInvoicesList((current) => uniq([...current, ...newInvoices]));
    setInvoices('');
    setRanPrecheck(false);
  };

  function addFiles(files) {
    // in case there's anything in the textarea beforehand
    setInvoices('');

    if (files) {
      setFiles(files);
    }
  }

  const clearFiles = () => {
    fileUploaderRef.current.clearFiles();
    setFiles([]);
  };

  function handleHideRunPrecheckAlert() {
    setShowRunPrecheckBanner(false);
  }

  function processPayload() {
    const validationErrors = {};
    if (invoicesList.length === 0 && files.length === 0) {
      validationErrors.referenceNumbers = t('PROVIDE_ATLEAST_ONE_REF_NUM');
    }
    setErrors(validationErrors);

    return { validationErrors };
  }

  async function handleRunPrecheck() {
    const { validationErrors } = processPayload();

    if (Object.keys(validationErrors).length > 0) {
      return;
    }
    try {
      setRanPrecheck(false);
      setResults([]);
      setRunningPrecheck(true);
      const result = await runPrecheck({
        userType: 'shipper',
        referenceNumbers: invoicesList,
        events: ['invoice_created'],
      });
      setShipperStatusList(result.entity.reportData);
      setRanPrecheck(true);
    } catch (e) {
      console.error(e);
    } finally {
      setRunningPrecheck(false);
    }
  }
  function handleHideEDIResendAlert() {
    setResendEDISuccess(false);
    setResendEDIError(false);
  }

  function handleReset() {
    setInvoices('');
    setRanPrecheck(false);
    setInvoicesList([]);
    clearFiles();
    setShipperStatusList([]);
    setResults([]);
    setResendEDISuccess(false);
    setResendEDIError(false);
    setShowRunPrecheckBanner(true);
  }

  async function handleUpload() {
    const { validationErrors } = processPayload();

    if (files.length !== 1) {
      validationErrors.invalid_file_amount = t('ATTACH_VALID_CSV_FILE');
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setUploadPending(true);
    const [file] = files;
    try {
      await resendEDIInvoicesBulk({
        file,
      });
      showToast(t('CSV_UPLOAD_SUCCESSFUL'), t('DATA_UPLOADED_WAIT_FOR_EMAIL'));
      setResendEDISuccess(true);
      setResendEDISuccessMessage(t('DATA_UPLOADED_WAIT_FOR_EMAIL'));
    } catch (e) {
      const errorMsg = e?.response?.body?.message || 'SOMETHING_WENT_WRONG';
      showToast(t('CSV_UPLOAD_FAILED'), t(sluggify(errorMsg), 'error'));
      setResendEDIError(t(sluggify(errorMsg)));
    } finally {
      setUploadPending(false);
    }
  }

  async function handleResendInvoiceEvents() {
    const { validationErrors } = processPayload();

    if (Object.keys(validationErrors).length > 0) {
      return;
    }
    try {
      setResendingEvents(true);
      setResendEDIError(false);
      setResendEDISuccess(false);

      const result = await resendEDIInvoices({
        invoice_numbers: invoicesList,
        events: ['invoice_created'],
      });
      setResults(result.entity.reportData);
      setShipperStatusList([]);
      setResendEDISuccess(true);
    } catch (e) {
      console.error(e);
      setResendEDIError(true);
    } finally {
      setInvoices('');
      setResendingEvents(false);
    }
  }

  function isFormEmpty() {
    return invoicesList.length === 0 && files.length === 0;
  }

  function isRunPrecheckDisabled() {
    return isFormEmpty() || runningPrecheck;
  }

  function isResendDisabled() {
    return isFormEmpty() || resendingEvents;
  }

  function renderAlerts() {
    let type;
    let message;
    if (resendEDISuccess) {
      type = 'success';
      message = resendEDISuccessMessage || t('RESEND_EDI_SUCCESS');
    } else if (resentEDIError) {
      type = 'error';
      message = t('RESEND_EDI_ERROR');
    }
    return (
      <div>
        {message && (
          <Alert
            className="mb-4"
            type={type}
            onRemove={handleHideEDIResendAlert}
          >
            {message}
          </Alert>
        )}
      </div>
    );
  }

  function renderResults() {
    if (results.length === 0) {
      return null;
    }

    return (
      <div className="">
        {renderAlerts()}
        <CustomerStatus userType="shipper" rows={results} />
      </div>
    );
  }
  function renderShipperRows() {
    if (shipperStatusList.length === 0) {
      return null;
    }

    return (
      <div className="">
        {showRunPrecheckBanner && (
          <Alert
            type="info"
            className="mb-4"
            onRemove={handleHideRunPrecheckAlert}
          >
            {t('PRECHECK_MESSAGE')}
          </Alert>
        )}
        <CustomerStatus userType="shipper" rows={shipperStatusList} />
      </div>
    );
  }
  function renderInvoices() {
    if (invoicesList.length === 0) {
      return null;
    }

    const label = t('INVOICE', { count: 2 });
    return (
      <div className="mt-6">
        <ReferenceNumberTable
          referenceNumbers={invoicesList}
          onRemove={removeInvoice}
          label={label}
        />
      </div>
    );
  }
  function renderErrors() {
    if (Object.keys(errors).length === 0) {
      return null;
    }

    return (
      <Alert type="error" className="my-4">
        <ul>
          {Object.values(errors).map((error) => (
            <li key={error}>{error}</li>
          ))}
        </ul>
      </Alert>
    );
  }
  return (
    <div className="">
      <div className="">
        <div className="bg-white shadow rounded-md">
          <div className="px-4 py-5 sm:p-6">
            <label className="mb-3 form-label">
              {t('PROVIDE')} {t('CSV')}
            </label>
            <FileUploader
              fileTypes={CSV_FILE_TYPES}
              fileTypesLabel={t('CSV')}
              onChange={addFiles}
              ref={fileUploaderRef}
              multiple
            />
            <div className="separator mt-4 mb-4">
              <div className="separator-text form-label">
                {t('OR').toUpperCase()}
              </div>
            </div>
            <label className="form-label mb-2 block">
              {t('INVOICES_LABEL')}
            </label>
            <div>
              <textarea
                className="form-field resize-none mb-4"
                rows={3}
                type="text"
                value={invoices}
                placeholder={t('COMMA_OR_LINE_SEPARATED_LIST')}
                onChange={onChangeInvoices}
              />

              <div className="flex justify-end mt-4">
                <button
                  disabled={!Invoices}
                  className="button button-hollow-green"
                  onClick={addInvoices}
                >
                  {t('ADD')}
                </button>
              </div>
              {renderInvoices()}
            </div>
          </div>

          {renderErrors()}
          <div className="p-4 flex justify-end mt-6 space-x-2 bg-gray-100">
            {files.length > 0 ? (
              <div className="">
                <div className="">{!uploadPending && renderAlerts()}</div>
                <div className="p-4 flex justify-end space-x-2 bg-gray-100">
                  <button
                    className="button button-secondary"
                    onClick={handleReset}
                  >
                    {t('RESET')}
                  </button>
                  <button
                    className="button button-green"
                    disabled={isResendDisabled()}
                    onClick={handleUpload}
                  >
                    {uploadPending && (
                      <LoadingCircle text={t('UPLOADING') + '...'} />
                    )}
                    {!uploadPending && (
                      <span>
                        {t('UPLOAD')} {t('CSV')}
                      </span>
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <button
                className="button button-green"
                disabled={isRunPrecheckDisabled()}
                onClick={handleRunPrecheck}
              >
                {runningPrecheck && (
                  <LoadingCircle text={t('CHECKING') + '...'} />
                )}
                {!runningPrecheck && <span>{t('CONTINUE')}</span>}
              </button>
            )}
          </div>
        </div>
      </div>
      {ranPrecheck && (
        <div className="">
          <div className="">{renderShipperRows()}</div>
          <div className="">{renderResults()}</div>
          <div className="p-4 flex justify-end space-x-2 bg-gray-100">
            <button className="button button-secondary" onClick={handleReset}>
              {t('RESET')}
            </button>
            <button
              className="button button-green"
              disabled={isResendDisabled()}
              onClick={handleResendInvoiceEvents}
            >
              {resendingEvents && (
                <LoadingCircle text={t('SENDING_EVENTS') + '...'} />
              )}
              {!resendingEvents && <span>{t('RESEND')}</span>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
